import { computed, inject, Injectable, signal } from '@angular/core';
import { Cart, ITEM_CART_TYPE } from '../models/cart.model';
import { instanceOfPortalProduct, isEcashItem, isFreeItem, isMerchantItem, isPointsItem } from './cart.helper';
import { MarketplaceItemV2 } from '../../marketplace/marketplace-v2/models/marketplace-item-v2.model';
import { MarketplaceItem } from '../../marketplace/models/marketplace-item.model';
import { PortalProduct } from '../../portal-products/models/portal-product.model';
import { MemberQuery } from '../../../account/member/member.query';
import {
  UnifiedCheckoutBasket,
  UnifiedCheckoutBasketRequest,
  UnifiedCheckoutPortalProductItem,
  UnifiedCheckoutPortalProductRequest,
  UnifiedCheckoutRewardItem,
  UnifiedCheckoutRewardRequest,
} from '../models/unified-checkout.model';

/**
 * How a summary line is paid for.
 *
 * A basket line carries MerchantPrice, PointsPrice and EcashPrice at once and
 * only one of them is what the member actually pays, so the kind is resolved
 * once when the line is mapped rather than re-derived in the template.
 */
export enum SUMMARY_PRICE_KIND {
  /** Real money, rendered as currency. */
  MERCHANT,
  /** Loyalty points, rendered as a plain number. */
  POINTS,
  /** E-cash balance, rendered as currency. */
  ECASH,
  /** Nothing payable on any of the three. */
  FREE,
}

/** A basket line as shown in a checkout order summary panel. */
export interface UnifiedCheckoutSummaryItem {
  id: number;
  type: ITEM_CART_TYPE;
  quantity: number;
  /**
   * Line total for the price kind the member is paying in, already multiplied
   * by quantity. Minor units for MERCHANT and ECASH, whole points for POINTS.
   */
  totalPrice: number;
  /** Which of the three prices `totalPrice` came from, and so how to render it. */
  priceKind: SUMMARY_PRICE_KIND;
  /** Line total in merchant minor units, 0 when the line is not paid in money. */
  totalMerchantPrice: number;
  /** Line total in points, 0 when the line is not paid in points. */
  totalPointsPrice: number;
  /** Line total in e-cash minor units, 0 when the line is not paid in e-cash. */
  totalEcashPrice: number;
  displayName: string;
  imageUrl?: string;
}

/**
 * Where the basket being checked out came from.
 *
 * 'cart' is a checkout started from the cart, so completing it empties the cart. 'direct' is a
 * single item bought straight from its details page, which never entered the cart - clearing the
 * cart then would throw away items the member had not bought.
 */
export type CheckoutSource = 'cart' | 'direct';

/** Where the source is kept across a payment provider redirect - see the note on `source`. */
const CHECKOUT_SOURCE_KEY = 'checkout_v2_source';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private memberQuery = inject(MemberQuery);

  items = signal<Cart[]>([]);

  /**
   * Where the current checkout started. Seeded from session storage because a provider redirect
   * leaves the app and can return to a fresh page load, which would otherwise lose it and leave a
   * real cart checkout unable to empty the cart.
   */
  readonly source = signal<CheckoutSource>(this.readStoredSource());

  /**
   * Whether the current checkout started from the cart, and so should empty it once the order
   * completes. Reads better at the call site than comparing the source, and keeps the string
   * literal inside this service.
   */
  readonly isCartCheckout = computed(() => this.source() === 'cart');

  readonly summary = computed(() => {
    const merchantItems = this.items()
      .filter((entity) => isMerchantItem(entity) && this.isItemFromCurrentMember(entity))
      .map(this.mapAsMarketplaceItemCart);
    const ecashItems = this.items()
      .filter((entity) => isEcashItem(entity) && this.isItemFromCurrentMember(entity))
      .map(this.mapAsMarketplaceItemCart);
    const pointsItems = this.items()
      .filter((entity) => isPointsItem(entity) && this.isItemFromCurrentMember(entity))
      .map(this.mapAsMarketplaceItemCart);
    const productItems = this.items()
      .filter((entity) => instanceOfPortalProduct(entity.productData) && this.isItemFromCurrentMember(entity))
      .map(this.mapAsPortalProductCart);
    // A free reward has no ecash, points or merchant value, so it matches none
    // of the buckets above. Without its own it would be dropped from the basket
    // entirely and never reach api/checkout.
    const freeItems = this.items()
      .filter((entity) => isFreeItem(entity) && this.isItemFromCurrentMember(entity))
      .map(this.mapAsMarketplaceItemCart);
    return {
      merchantItems: {
        items: merchantItems,
        totalPrice: merchantItems.reduce(
          (accumVal, item) => accumVal + item.purchaseQuantity * item.productData.MerchantValue,
          0
        ),
      },
      ecashItems: {
        items: ecashItems,
        totalEcash: ecashItems.reduce((accumVal, item) => accumVal + item.purchaseQuantity * item.productData.EcashValue, 0),
      },
      pointsItems: {
        items: pointsItems,
        totalPoints: pointsItems.reduce((accumVal, item) => accumVal + item.purchaseQuantity * item.productData.PointValue, 0),
      },
      productItems: {
        items: productItems,
        totalPrice: productItems.reduce((accumVal, item) => accumVal + item.purchaseQuantity * item.productData.MerchantPrice, 0),
      },
      freeItems: {
        items: freeItems,
        totalPrice: 0,
      },
      totalItems: merchantItems.length + ecashItems.length + pointsItems.length + productItems.length + freeItems.length,
      items: [...merchantItems, ...pointsItems, ...ecashItems, ...freeItems, ...productItems],
    };
  });

  /**
   * The items being checked out as a start-checkout basket for `api/checkout`,
   * split by item kind.
   *
   * Unified checkout can hold both kinds at once and each goes in its own list.
   * The two are keyed differently - a reward by `Id`, a portal product by
   * `ProductId` - so they cannot share a mapping.
   */
  readonly checkoutBasket = computed<UnifiedCheckoutBasketRequest>(() => {
    const rewards: UnifiedCheckoutRewardRequest[] = [];
    const portalProducts: UnifiedCheckoutPortalProductRequest[] = [];
    const memberId = this.memberQuery.getUserContext().memberId;

    this.summary().items.forEach((item) => {
      if (instanceOfPortalProduct(item.productData)) {
        portalProducts.push({
          productId: item.productData.ProductId,
          quantity: item.purchaseQuantity,
          memberId,
        });
      } else {
        // GameNumber only exists on the v1 MarketplaceItem models, not on
        // MarketplaceItemV2, so it is read off the reward rather than assumed
        // and falls back to 0 for a v2 item.
        const reward = item.productData as Partial<MarketplaceItem> & MarketplaceItemV2;
        rewards.push({
          id: reward.Id,
          gameNumber: reward.GameNumber ?? 0,
          quantity: item.purchaseQuantity,
          attributeMappingId: item.attributes?.AttributeMappingId,
          customFields: item.customFields,
        });
      }
    });

    return { rewards, portalProducts };
  });

  /**
   * The basket lines of a checkout response as order summary items, rewards
   * first and portal products after, matching the order of the response.
   *
   * Lines carry their own `Title` and `Image`, so the summary renders from the
   * response rather than the cart.
   */
  toSummaryItems(basket: UnifiedCheckoutBasket | undefined | null): UnifiedCheckoutSummaryItem[] {
    if (!basket) {
      return [];
    }

    const rewards = (basket.Rewards ?? []).map((line) => this.toSummaryItem(line, ITEM_CART_TYPE.MARKETPLACE));
    const products = (basket.PortalProducts ?? []).map((line) => this.toSummaryItem(line, ITEM_CART_TYPE.PRODUCT));
    return [...rewards, ...products];
  }

  private toSummaryItem(
    line: UnifiedCheckoutRewardItem | UnifiedCheckoutPortalProductItem,
    type: ITEM_CART_TYPE
  ): UnifiedCheckoutSummaryItem {
    // The line's own Title and Image are used where present. The cart is still
    // consulted as a fallback, for a member arriving with a checkout already in
    // progress and so no cart to have added anything from.
    const productData = this.findCartProductData(line.Id, type);

    const quantity = line.Quantity;
    const totalMerchantPrice = (line.MerchantPrice ?? 0) * quantity;
    const totalPointsPrice = (line.PointsPrice ?? 0) * quantity;
    const totalEcashPrice = (line.EcashPrice ?? 0) * quantity;
    const priceKind = this.resolvePriceKind(totalMerchantPrice, totalPointsPrice, totalEcashPrice);

    return {
      id: line.Id,
      type,
      quantity,
      priceKind,
      totalPrice: this.priceForKind(priceKind, totalMerchantPrice, totalPointsPrice, totalEcashPrice),
      totalMerchantPrice,
      totalPointsPrice,
      totalEcashPrice,
      displayName: line.Title ?? productData?.DisplayName ?? '',
      imageUrl: line.Image ?? this.cartImageUrl(productData, type),
    };
  }

  /**
   * Which price the member is actually paying in.
   *
   * E-cash first, then merchant, then points, matching the precedence the v2
   * cart checkout page renders with - a line can carry more than one non-zero
   * price and the two pages must agree on which one is shown.
   */
  private resolvePriceKind(merchant: number, points: number, ecash: number): SUMMARY_PRICE_KIND {
    if (ecash > 0) {
      return SUMMARY_PRICE_KIND.ECASH;
    }
    if (merchant > 0) {
      return SUMMARY_PRICE_KIND.MERCHANT;
    }
    if (points > 0) {
      return SUMMARY_PRICE_KIND.POINTS;
    }
    return SUMMARY_PRICE_KIND.FREE;
  }

  /** The total belonging to a resolved price kind. FREE has nothing payable, so 0. */
  private priceForKind(kind: SUMMARY_PRICE_KIND, merchant: number, points: number, ecash: number): number {
    switch (kind) {
      case SUMMARY_PRICE_KIND.ECASH:
        return ecash;
      case SUMMARY_PRICE_KIND.MERCHANT:
        return merchant;
      case SUMMARY_PRICE_KIND.POINTS:
        return points;
      default:
        return 0;
    }
  }

  /**
   * The cart item behind a basket line, when there is one.
   *
   * A portal product is keyed by ProductId in the cart but comes back as Id on
   * the basket line, so the two kinds cannot share one lookup key.
   */
  private findCartProductData(lineId: number, type: ITEM_CART_TYPE): Partial<MarketplaceItemV2 & PortalProduct> | undefined {
    const isProduct = type === ITEM_CART_TYPE.PRODUCT;
    return this.summary()
      .items.map((item) => item.productData as Partial<MarketplaceItemV2 & PortalProduct>)
      .find((data) => (isProduct ? data?.ProductId : data?.Id) === lineId);
  }

  /** The image field differs by kind: PrimaryImage on a product, ImageURL on a reward. */
  private cartImageUrl(
    productData: Partial<MarketplaceItemV2 & PortalProduct> | undefined,
    type: ITEM_CART_TYPE
  ): string | undefined {
    return type === ITEM_CART_TYPE.PRODUCT ? productData?.PrimaryImage : productData?.ImageURL;
  }

  /**
   * Starts a checkout with the given items. `source` records where they came from, so the success
   * page knows whether emptying the cart is the right thing to do - it defaults to 'direct', the
   * safe option, since that clears nothing.
   */
  checkOutItems(items: Cart[], source: CheckoutSource = 'direct'): void {
    this.clearAll();
    this.items.set(items);
    this.setSource(source);
  }

  /**
   * Starts a checkout for items that came from the cart, so completing the order empties it.
   *
   * Its own method rather than a `source` argument at each call site: the caller states what it is
   * doing instead of passing a flag it has to get right, and a new cart entry point that forgets
   * would otherwise silently leave the cart full after a purchase.
   */
  checkOutToCart(items: Cart[]): void {
    this.checkOutItems(items, 'cart');
  }

  clearAll(): void {
    this.items.set([]);
    this.setSource('direct');
  }

  private setSource(source: CheckoutSource): void {
    this.source.set(source);

    // Wrapped because storage throws in private browsing and when site data is blocked; the source
    // simply falls back to 'direct' there, which clears nothing.
    try {
      sessionStorage.setItem(CHECKOUT_SOURCE_KEY, source);
    } catch {
      // Ignored - see above.
    }
  }

  private readStoredSource(): CheckoutSource {
    try {
      return sessionStorage.getItem(CHECKOUT_SOURCE_KEY) === 'cart' ? 'cart' : 'direct';
    } catch {
      return 'direct';
    }
  }

  private isItemFromCurrentMember = (entity: Cart) => entity.memberId == this.memberQuery.getUserContext().memberId;
  private mapAsMarketplaceItemCart = (entity: Cart) => ({
    ...entity,
    type: ITEM_CART_TYPE.MARKETPLACE,
    productData: entity.productData as MarketplaceItemV2,
  });
  private mapAsPortalProductCart = (entity: Cart) => ({
    ...entity,
    type: ITEM_CART_TYPE.PRODUCT,
    productData: entity.productData as PortalProduct,
  });
}
